// One look for every tooltip of the app (5-nov-2026): dark box, white monospace text,
// black border, flat corners. Explanatory tips (HoverTip ⓘ) use 12 px text; chart
// tooltips (Recharts `content`) 11 px. Neither can be wider than the screen.

export const TIP_BOX = "bg-[#111111] text-white font-mono border border-black shadow-md";
// Secondary text inside a tooltip (dates, units, hints): readable on the dark box.
export const TIP_MUTED = "#A89E8E";
export const TIP_RULE = "border-white/15";
// Up/down colours readable on the dark box (the app's #1D7044 / #B32A22 are too dark).
export const TIP_UP = "#5BC48A";
export const TIP_DOWN = "#F08A82";

// Recharts keeps its tooltip inside the chart; the width cap keeps a long one inside a
// phone screen too.
export const CHART_TIP = `${TIP_BOX} text-[11px] leading-snug px-2.5 py-2 max-w-[min(20rem,calc(100vw-2rem))] break-words`;

// Props for a Recharts <Tooltip> without custom `content` (default item list).
export const CHART_TIP_PROPS = {
    contentStyle: {
        background: "#111111", border: "1px solid #000", borderRadius: 0,
        fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 11, lineHeight: 1.4,
        padding: "8px 10px", maxWidth: "min(20rem, calc(100vw - 2rem))", whiteSpace: "normal",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
    },
    labelStyle: { color: TIP_MUTED, marginBottom: 2 },
    itemStyle: { color: "#fff", padding: 0 },
    wrapperStyle: { zIndex: 30, outline: "none" },
};

export const TIP_MARGIN = 8; // minimum distance to the screen edges
const MIN_HEIGHT = 96;       // below this, overlapping the trigger beats a tiny box

// Where to put a fixed tooltip next to `anchor` (a DOMRect) so it stays on screen:
// below if it fits, above if it fits there, otherwise on the roomier side with its
// height capped (it scrolls). `w`/`h` = the tooltip's natural size (scrollHeight).
export function placeTip(anchor, w, h, gap = 8) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = TIP_MARGIN;
    const left = Math.max(m, Math.min(anchor.left, vw - w - m));
    const below = vh - anchor.bottom - gap - m;
    const above = anchor.top - gap - m;
    if (h <= below) return { top: anchor.bottom + gap, left, maxHeight: null, scroll: false };
    if (h <= above) return { top: anchor.top - gap - h, left, maxHeight: null, scroll: false };
    const room = Math.max(below, above);
    if (room < MIN_HEIGHT) {
        const maxHeight = vh - 2 * m;
        return { top: Math.max(m, Math.min(anchor.bottom + gap, vh - m - Math.min(h, maxHeight))), left, maxHeight, scroll: h > maxHeight };
    }
    return below >= above
        ? { top: anchor.bottom + gap, left, maxHeight: room, scroll: true }
        : { top: m, left, maxHeight: room, scroll: true };
}

// Inline style of a fixed tooltip: never wider than the screen, never taller once placed.
export const tipFixedStyle = (pos, maxWidth) => ({
    position: "fixed",
    top: pos.top,
    left: pos.left,
    zIndex: 10000, // above modals and fullscreen views (rendered in a portal)
    maxWidth: `min(${maxWidth}px, calc(100vw - ${2 * TIP_MARGIN}px))`,
    maxHeight: pos.maxHeight != null ? pos.maxHeight : `calc(100vh - ${2 * TIP_MARGIN}px)`,
    overflowY: "auto",
    overflowWrap: "anywhere",
    overscrollBehavior: "contain",
});
